<template>
    <rkm-form
        v-if="loaded"
        ref="Form"
        show-legend
    >
        <rkm-area title="De (Origem)">
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :repository="repositories.reterritorializacao.usuario"
                        :request-query-string-params="{ from: 'origem' }"
                        :search-fields="usuarioSearchFields"
                        :text-field="u => u.nome"
                        :validations="validations.origem.usuario"
                        label="Profissional"
                        v-model="model.origem.usuario"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="!model.origem.usuario"
                        :options="form.origem.unidades"
                        :validations="validations.origem.unidade"
                        @change="handleOrigemUnidadeChange"
                        label="Unidade"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.origem.unidadeId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="!model.origem.unidadeId"
                        :options="form.origem.infraestruturas"
                        :validations="validations.origem.infraestrutura"
                        @change="handleOrigemInfraestruturaChange"
                        label="Local de atendimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.origem.infraestruturaId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="!model.origem.infraestruturaId"
                        :options="form.origem.equipes"
                        :validations="validations.origem.equipe"
                        @change="handleOrigemEquipeChange"
                        label="Equipe"
                        show-default-option
                        text-field="descricao"
                        v-model="model.origem.equipeId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="!model.origem.infraestruturaId"
                        :options="form.origem.ocupacoes"
                        :validations="validations.origem.ocupacao"
                        label="CBO"
                        show-default-option
                        text-field="descricao"
                        v-model="model.origem.ocupacaoId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area title="Para (Destino)">
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input
                        :value="model.origem.unidadeId
                            ? form.origem.unidades.find(u => u.id === model.origem.unidadeId).nomeFantasia
                            : null"
                        disabled
                        label="Unidade"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        :value="model.origem.infraestruturaId
                            ? form.origem.infraestruturas.find(u => u.id === model.origem.infraestruturaId).nome
                            : null"
                        disabled
                        label="Local de atendimento"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="!model.origem.equipeId"
                        :options="form.destino.equipes"
                        :validations="validations.destino.equipe"
                        @change="handleDestinoEquipeChange"
                        label="Equipe"
                        show-default-option
                        text-field="descricao"
                        v-model="model.destino.equipeId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="!model.destino.equipeId"
                        :repository="repositories.reterritorializacao.usuario"
                        :request-query-string-params="{ from: 'destino', equipe_id: model.destino.equipeId }"
                        :search-fields="usuarioSearchFields"
                        :text-field="u => u.nome"
                        :validations="validations.destino.usuario"
                        label="Profissional"
                        v-model="model.destino.usuario"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="!model.destino.usuario"
                        :options="form.destino.ocupacoes"
                        :validations="validations.destino.ocupacao"
                        label="CBO"
                        show-default-option
                        text-field="descricao"
                        v-model="model.destino.ocupacaoId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area title="Histórico">
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>
            </template>

            <rkm-table
                :fields="fields"
                :items="form.historico"
                sort-by="dataHora"
                sort-desc
            />
        </rkm-area>
    </rkm-form>
</template>

<script>
// Mixins.
import { repository, error, stringCase, formats } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        validations() {
            return {
                origem: {
                    usuario: { required: true },
                    unidade: { required: true },
                    infraestrutura: { required: true },
                    equipe: { required: true },
                    ocupacao: { required: true },
                },
                destino: {
                    equipe: { required: true },
                    usuario: { required: true },
                    ocupacao: { required: true },
                },
            }
        },
        optionalColumns() {
            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {
            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        columns() {
            return [
                { key: 'dataHora', label: 'Data/hora', formatter: v => this.formatDateTime(v), filterByFormatted: true, sortable: true, fixed: true },
                { key: 'usuarioAlteracaoNome', label: 'Usuário responsável pela alteração', sortable: true },
                { key: 'origemUsuarioNome', label: 'Profissional (Origem)', sortable: true },
                { key: 'origemUnidadeNomeFantasia', label: 'Unidade (Origem)', sortable: true },
                { key: 'origemInfraestruturaNome', label: 'Local de atendimento (Origem)', sortable: true },
                { key: 'origemEquipeDescricao', label: 'Equipe (Origem)', sortable: true },
                { key: 'origemOcupacaoDescricao', label: 'CBO (Origem)', sortable: true },
                { key: 'destinoUsuarioNome', label: 'Profissional (Destino)', sortable: true },
                { key: 'destinoUnidadeNomeFantasia', label: 'Unidade (Destino)', sortable: true },
                { key: 'destinoInfraestruturaNome', label: 'Local de atendimento (Destino)', sortable: true },
                { key: 'destinoEquipeDescricao', label: 'Equipe (Destino)', sortable: true },
                { key: 'destinoOcupacaoDescricao', label: 'CBO (Destino)', sortable: true },
                { key: 'quantidadeCadastrosIndividuais', label: 'Quantidade de cadastros individuais', class: 'text-center', sortable: true },
                { key: 'quantidadeCadastrosDomiciliares', label: 'Quantidade de cadastros domiciliares', class: 'text-center', sortable: true },
            ]
        }
    },
    created() {
        this.repositories = {
            reterritorializacao: this.getConectaSusRepository('atencaoBasica.reterritorializacao'),
        }

        this.load()
    },
    data: () => ({
        loaded: false,
        form: null,
        model: null,
        usuarioSearchFields: [
            { key: 'cidadao.nome', searchable: true }
        ],
        selectedAll: false,
        indeterminate: true,
        selectedCols: [
            'usuarioAlteracaoNome',
            'origemUsuarioNome',
            'origemEquipeDescricao',
            'origemOcupacaoDescricao',
            'destinoUsuarioNome',
            'destinoEquipeDescricao',
            'destinoOcupacaoDescricao',
            'quantidadeCadastrosIndividuais',
            'quantidadeCadastrosDomiciliares',
        ],
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.reterritorializacao.create()
                .then(({ data }) => {
                    this.form = data.form
                    this.model = data.model

                    this.addWatchers()

                    this.$emit('model-load', this.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {
            this.$watch('model.origem.usuario', this.handleOrigemUsuarioChange)
            this.$watch('model.destino.usuario', this.handleDestinoUsuarioChange)
        },
        handleOrigemUsuarioChange() {
            this.form.origem.unidades = []
            this.form.origem.infraestruturas = []
            this.form.origem.equipes = []
            this.form.origem.ocupacoes = []
            this.model.origem.unidadeId = null
            this.model.origem.infraestruturaId = null
            this.model.origem.equipeId = null
            this.model.origem.ocupacaoId = null

            this.form.destino.equipes = []
            this.form.destino.ocupacoes = []
            this.model.destino.equipeId = null
            this.model.destino.usuario = null
            this.model.destino.ocupacaoId = null

            if (!this.model.origem.usuario) return

            this.loadAfterOrigemUsuario()
        },
        loadAfterOrigemUsuario() {
            const params = {
                listar: 'unidades',
                usuarioId: this.model.origem.usuario.id
            }

            this.setLoading()

            this.repositories.reterritorializacao.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.origem.unidades = data.unidades
                    this.form.origem.infraestruturas = data.infraestruturas
                    this.form.origem.equipes = data.equipes
                    this.form.origem.ocupacoes = data.ocupacoes
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleOrigemUnidadeChange() {
            this.form.origem.infraestruturas = []
            this.form.origem.equipes = []
            this.form.origem.ocupacoes = []
            this.model.origem.infraestruturaId = null
            this.model.origem.equipeId = null
            this.model.origem.ocupacaoId = null

            this.form.destino.equipes = []
            this.form.destino.ocupacoes = []
            this.model.destino.equipeId = null
            this.model.destino.usuario = null
            this.model.destino.ocupacaoId = null

            if (!this.model.origem.unidadeId) return

            this.loadAfterOrigemUnidade()
        },
        loadAfterOrigemUnidade() {
            const params = {
                listar: 'infraestruturas',
                usuarioId: this.model.origem.usuario.id,
                unidadeId: this.model.origem.unidadeId
            }

            this.setLoading()

            this.repositories.reterritorializacao.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.origem.infraestruturas = data.infraestruturas
                    this.form.origem.equipes = data.equipes
                    this.form.origem.ocupacoes = data.ocupacoes
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleOrigemInfraestruturaChange() {
            this.form.origem.equipes = []
            this.form.origem.ocupacoes = []
            this.model.origem.equipeId = null
            this.model.origem.ocupacaoId = null

            this.form.destino.equipes = []
            this.form.destino.ocupacoes = []
            this.model.destino.equipeId = null
            this.model.destino.usuario = null
            this.model.destino.ocupacaoId = null

            if (!this.model.origem.infraestruturaId) return

            this.loadAfterOrigemInfraestrutura()
        },
        loadAfterOrigemInfraestrutura() {
            const params = {
                listar: 'equipes',
                usuarioId: this.model.origem.usuario.id,
                infraestruturaId: this.model.origem.infraestruturaId
            }

            this.setLoading()

            this.repositories.reterritorializacao.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.origem.equipes = data.equipes
                    this.form.origem.ocupacoes = data.ocupacoes
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleOrigemEquipeChange() {
            this.form.destino.equipes = []
            this.form.destino.ocupacoes = []
            this.model.destino.equipeId = null
            this.model.destino.usuario = null
            this.model.destino.ocupacaoId = null

            if (!this.model.origem.equipeId) return

            this.loadDestinoEquipes()
        },
        loadDestinoEquipes() {
            const params = {
                listar: 'equipes-destino',
                infraestruturaId: this.model.origem.infraestruturaId,
                areaId: this.form.origem.equipes.find(e => e.id === this.model.origem.equipeId).areaId,
            }

            this.setLoading()

            this.repositories.reterritorializacao.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.destino.equipes = data.equipes
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleDestinoEquipeChange() {
            this.form.destino.ocupacoes = []
            this.model.destino.usuario = null
            this.model.destino.ocupacaoId = null
        },
        handleDestinoUsuarioChange() {
            this.form.destino.ocupacoes = []
            this.model.destino.ocupacaoId = null

            if (!this.model.destino.usuario) return

            this.loadAfterDestinoUsuario()
        },
        loadAfterDestinoUsuario() {
            const params = {
                listar: 'ocupacoes',
                infraestruturaId: this.model.origem.infraestruturaId,
                usuarioId: this.model.destino.usuario.id,
                from: 'destino'
            }

            this.setLoading()

            this.repositories.reterritorializacao.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.destino.ocupacoes = data.ocupacoes
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save() {
            this.$refs.Form.validate().then(valid => {
                if (!valid) return

                if (this.model.origem.equipeId === this.model.destino.equipeId
                    && this.model.origem.usuario.id === this.model.destino.usuario.id
                    && this.model.origem.ocupacaoId === this.model.destino.ocupacaoId
                ) {
                    this.showValidationNotification('Os dados de origem e destino não podem ser iguais.')

                    return
                }

                this.showWarningConfirmation(
                    'Atenção',
                    `Ao reterritorializar, todos os cadastros individuais e cadastros domicilares serão atualizados
                    e estarão disponíveis para serem reenviados para o e-SUS. Deseja continuar?`,
                    async () => {
                        this.$nextTick().then(() => this.showWarningConfirmation(
                            'Atenção',
                            'Esta ação não poderá ser desfeita, deseja mesmo continuar?',
                            async () => this.store()
                        ))
                    }
                )
            })
        },
        store() {
            this.setLoading()

            this.repositories.reterritorializacao.store(this.prepareData())
                .then(({ data }) => {
                    this.showSaveNotification(`
                        Reterritorialização feita com sucesso!<br><br>
                        Quantidade de cadastros individuais: <strong>${data.quantidadeCadastrosIndividuais}</strong><br>
                        Quantidade de cadastros domiciliares: <strong>${data.quantidadeCadastrosDomiciliares}</strong>
                    `)

                    this.$emit('save')
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        prepareData() {
            return this.camelObjectKeysToSnakeRecursive({
                origem: {
                    usuarioId: this.model.origem.usuario.id,
                    equipeId: this.model.origem.equipeId,
                    ocupacaoId: this.model.origem.ocupacaoId,
                },
                destino: {
                    usuarioId: this.model.destino.usuario.id,
                    equipeId: this.model.destino.equipeId,
                    ocupacaoId: this.model.destino.ocupacaoId,
                },
            })
        },
        selectAll(selected) {
            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mixins: [repository, error, stringCase, formats],
    watch: {
        selectedCols(newValue) {
            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>